import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CreditCard, CheckCircle2, XCircle, Clock, AlertCircle, Loader2 } from 'lucide-react'
import { publicPaymentApi } from '@/lib/api'
import { formatCurrency, formatDate } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import type { GatewayType } from '@/types'



export default function PublicPaymentPage() {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id')
  const isCancelled = searchParams.get('cancelled') === 'true'
  
  const [selectedGateway, setSelectedGateway] = useState<GatewayType | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Check if this is a success/cancel redirect
  const isSuccessRedirect = !!sessionId
  const isCancelRedirect = isCancelled

  // Fetch payment info
  const { data: paymentInfo, isLoading: isLoadingInfo, error: infoError } = useQuery({
    queryKey: ['public-payment-info', token],
    queryFn: () => publicPaymentApi.getPaymentInfo(token!),
    enabled: !!token && !isSuccessRedirect && !isCancelRedirect,
    retry: false,
  })

  // Fetch available gateways
  const { data: gateways = [], isLoading: isLoadingGateways } = useQuery({
    queryKey: ['public-payment-gateways', token],
    queryFn: () => publicPaymentApi.getAvailableGateways(token!),
    enabled: !!token && !isSuccessRedirect && !isCancelRedirect && !!paymentInfo && !paymentInfo.is_expired && !paymentInfo.is_already_paid,
  })

  // Fetch payment status for success redirect
  const { data: paymentStatus, isLoading: isLoadingStatus } = useQuery({
    queryKey: ['payment-status', sessionId],
    queryFn: () => publicPaymentApi.getPaymentStatus(sessionId!),
    enabled: !!sessionId,
  })

  // Auto-select first gateway if only one available
  useEffect(() => {
    if (gateways.length === 1 && !selectedGateway) {
      setSelectedGateway(gateways[0].gateway_type)
    }
  }, [gateways, selectedGateway])

  const handlePayment = async () => {
    if (!selectedGateway || !token) return

    setIsProcessing(true)
    setError(null)

    try {
      const result = await publicPaymentApi.initiateCheckout(token, selectedGateway)
      // Redirect to payment gateway
      window.location.href = result.checkout_url
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to initiate payment. Please try again.')
      setIsProcessing(false)
    }
  }

  // Success page
  if (isSuccessRedirect) {
    if (isLoadingStatus) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="flex flex-col items-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Verifying payment...</p>
            </CardContent>
          </Card>
        </div>
      )
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="flex flex-col items-center py-12">
            <div className="rounded-full bg-green-100 dark:bg-green-900 p-4 mb-6">
              <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-center mb-2">Payment Successful!</h1>
            <p className="text-muted-foreground text-center mb-6">
              {paymentStatus?.message || 'Thank you for your payment. A confirmation email will be sent shortly.'}
            </p>
            <Badge variant="default" className="bg-green-500">
              {paymentStatus?.status || 'COMPLETED'}
            </Badge>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Cancel page
  if (isCancelRedirect) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-900 dark:to-gray-800">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="flex flex-col items-center py-12">
            <div className="rounded-full bg-orange-100 dark:bg-orange-900 p-4 mb-6">
              <XCircle className="h-12 w-12 text-orange-600 dark:text-orange-400" />
            </div>
            <h1 className="text-2xl font-bold text-center mb-2">Payment Cancelled</h1>
            <p className="text-muted-foreground text-center mb-6">
              Your payment was cancelled. No charges were made.
            </p>
            <Button onClick={() => window.location.href = `/pay/${token}`}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Loading state
  if (isLoadingInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="flex flex-col items-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Loading payment details...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Error state
  if (infoError || !paymentInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 dark:from-gray-900 dark:to-gray-800">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="flex flex-col items-center py-12">
            <div className="rounded-full bg-red-100 dark:bg-red-900 p-4 mb-6">
              <AlertCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-center mb-2">Invalid Payment Link</h1>
            <p className="text-muted-foreground text-center">
              This payment link is invalid or has expired. Please contact the sender for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Already paid state
  if (paymentInfo.is_already_paid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="flex flex-col items-center py-12">
            <div className="rounded-full bg-green-100 dark:bg-green-900 p-4 mb-6">
              <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-center mb-2">Already Paid</h1>
            <p className="text-muted-foreground text-center">
              This invoice has already been paid. Thank you!
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Expired state
  if (paymentInfo.is_expired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-900 dark:to-gray-800">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="flex flex-col items-center py-12">
            <div className="rounded-full bg-gray-100 dark:bg-gray-800 p-4 mb-6">
              <Clock className="h-12 w-12 text-gray-600 dark:text-gray-400" />
            </div>
            <h1 className="text-2xl font-bold text-center mb-2">Link Expired</h1>
            <p className="text-muted-foreground text-center">
              This payment link has expired. Please contact the sender for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Main payment page
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 py-8 px-4">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Company Header */}
        <div className="text-center">
          {paymentInfo.company_logo && (
            <img
              src={paymentInfo.company_logo}
              alt={paymentInfo.company_name}
              className="h-16 mx-auto mb-4"
            />
          )}
          <h1 className="text-2xl font-bold">{paymentInfo.company_name}</h1>
        </div>

        {/* Invoice Details Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Invoice Payment
            </CardTitle>
            <CardDescription>Invoice #{paymentInfo.invoice_number}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-muted-foreground">Amount Due</span>
              <span className="text-2xl font-bold">{formatCurrency(paymentInfo.amount, paymentInfo.currency)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Due Date</span>
              <span>{formatDate(paymentInfo.due_date)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Payment Method Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Select Payment Method</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoadingGateways ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : gateways.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                No payment methods available. Please contact the sender.
              </div>
            ) : (
              gateways.map((gateway) => (
                <button
                  key={gateway.gateway_type}
                  onClick={() => setSelectedGateway(gateway.gateway_type)}
                  className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-colors ${
                    selectedGateway === gateway.gateway_type
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="font-medium">{gateway.display_name}</div>
                    {gateway.is_test_mode && (
                      <Badge variant="outline" className="text-xs">Test Mode</Badge>
                    )}
                  </div>
                  {selectedGateway === gateway.gateway_type && (
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                  )}
                </button>
              ))
            )}

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            <Button
              className="w-full mt-4"
              size="lg"
              onClick={handlePayment}
              disabled={!selectedGateway || isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pay {formatCurrency(paymentInfo.amount, paymentInfo.currency)}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Security Notice */}
        <p className="text-center text-xs text-muted-foreground">
          🔒 Secure payment powered by industry-leading encryption
        </p>
      </div>
    </div>
  )
}

